/**
 * How long a turn gets, as presets (F4, F9).
 *
 * One control, used twice - which F9 decided and the code did not yet do. The
 * lobby and the in-game host sheet each carried their own copy of this block
 * and their own copy of `duration`, character for character. They agreed today
 * because they were written together; nothing made them keep agreeing, and the
 * mockups had already drifted to a stepper in one of the two places.
 *
 * Presets rather than a stepper, everywhere. A stepper invites fiddling at the
 * worst possible moment - the host sheet opens *mid-game*, and nudging a number
 * one second at a time while three people wait is the wrong interaction. Most
 * hosts have no opinion on how many seconds a turn should be but can pick
 * between brisk and relaxed instantly: a preset is a decision, a stepper is a
 * calculation.
 *
 * The bounds come from the server, so no offered choice can be refused.
 */

import { SettingShell } from './SettingRow.js';

/** A limit as a person would say it: "30s", "1m 30s", "3m". */
function duration(ms: number): string {
  const seconds = Math.round(ms / 1000);
  if (seconds < 60) return `${String(seconds)}s`;
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  return rest === 0 ? `${String(minutes)}m` : `${String(minutes)}m ${String(rest)}s`;
}

export function TurnTimer({
  limits,
  current,
  onChoose,
}: {
  /** Bounds the server will accept. Absent means the server never said. */
  limits?: { min: number; default: number; max: number } | undefined;
  current: number;
  onChoose: (turnLimitMs: number) => void;
}) {
  if (!limits) return null;

  /*
   * The same row as every toggle beside it: mark, name, consequence, control.
   *
   * It used to be a label over a row of buttons, which in a column of setting
   * rows read as a different kind of thing - a heading and a form, rather than
   * one more choice. The three presets are a `.segmented`, the control the
   * mockups draw for a choice between a few, and the same one the join screen
   * uses for play-or-watch.
   */
  return (
    <SettingShell icon="clock" title="Turn timer" help="Play moves on when time runs out">
      <div className="segmented" role="group" aria-label="Turn timer">
        {[limits.min, limits.default, limits.max].map((ms) => (
          <button
            key={ms}
            type="button"
            className={`segment${current === ms ? ' chosen' : ''}`}
            onClick={() => onChoose(ms)}
            aria-pressed={current === ms}
          >
            {duration(ms)}
          </button>
        ))}
      </div>
    </SettingShell>
  );
}
